import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { AgreementsApi } from '../model/agreements.model';
import { AllianzAgentsResponse } from '../model/allianz-agents.model';
import AppError from '../model/app-error';
import { AppType } from '../model/app-type.model';
import {
  AsyncValidatorResponse,
  BlockValidatorResponse,
  BlocklistPolicyholderPayload,
  KnfLimitCheckPayload,
} from '../model/async-validation.model';
import { AttachmentsResponse } from '../model/attachments.model';
import { AuthCheckResponse } from '../model/auth-check';
import { AuthTokenPayload, AuthTokenResponse } from '../model/auth-token';
import { CalculationToClientConfirmPayload, CalculationToClientConfirmResponse } from '../model/calculation-to-client-confirm.model';
import { ConfirmPayload, ConfirmResponse } from '../model/confirm.model';
import { DefaultConfig } from '../model/default-config.model';
import { GlobtroterConfigResult, GlobtroterMenuResult, GlobtroterUserResult } from '../model/globtroter-config';
import { InsuredImportResponse } from '../model/insured-import.model';
import {
  AgentPremiumCalculationBody,
  AgentQuotationBody,
  DiscountCodeResponse,
  PremiumCalculationApi,
  PremiumCalculationBody,
  QuotationBody,
  QuotationResponse,
} from '../model/offer';
import {
  PaymentsConfigResponse,
  PaymentsPolicyPayload,
  PaymentsPolicyResponse,
  PaymentsTransactionResponse,
} from '../model/payments.model';
import { PurchaseAgentPayload, PurchaseAgentResponse } from '../model/purchase-agent.model';
import { RemoteAccessResponseError, RemoteAccessResponseSuccess } from '../model/remote-access.model';
import { RemoteCheckSmsTokenResponseError, RemoteCheckSmsTokenResponseSuccess } from '../model/remote-check-sms-token.model';
import { RemoteSendOtpResponseError, RemoteSendOtpResponseSuccess } from '../model/remote-send-otp.model';
import {
  AgentSaveCalculationPayload,
  LoadPolicyDataResponse,
  SaveCalculationPayload,
  SaveCalculationResponse,
} from '../model/save-calculation.model';
import { SaveContactPayload, SaveContactResponse } from '../model/save-contact.model';
import { SendStaticAttachmentsResponse } from '../model/send-static-attachments.model';
import { CacheService } from './cache.service';
import { ProcessType } from './process.service';

enum ApiDirectEndpoints {
  Config = 'globapidirect/ext/packages/AllianzTravelDirect/config',
  Agreements = 'globapidirect/ext/packages/AllianzTravelDirect/agreements',
  Quoatation = 'globapidirect/ext/packages/AllianzTravelDirect/quotation',
  SaveCalculation = 'globapidirect/ext/packages/AllianzTravelDirect/save-calculation',
  Confirm = 'globapidirect/ext/packages/AllianzTravelDirect/confirm',
  DiscountCode = 'globapidirect/ext/packages/AllianzTravelDirect/discountcode',
  PremiumCalculation = 'globapidirect/ext/packages/AllianzTravelDirect/premiumcalculation',
  PaymentsTransaction = 'globapidirect/public/policy-payment/transaction',
  PaymentsPolicy = 'globapidirect/public/policy-payment/transaction/policy',
  PaymentsConfig = 'globapidirect/public/policy-payment',
  Attachments = 'globapidirect/ext/packages/AllianzTravelDirect/attachments',
  SaveContact = 'globapidirect/ext/packages/AllianzTravelDirect/save-contact',
  PhoneNumberValidate = 'globapidirect/ext/packages/AllianzTravelDirect/validate/phone-number',
  EmailValidate = 'globapidirect/ext/packages/AllianzTravelDirect/validate/email',
  BlocklistCheck = 'globapidirect/ext/packages/AllianzTravelDirect/blocklistcheck',
}

export enum ApiAgentEndpoints {
  Config = 'globapi/packages/AllianzTravel/config',
  Purchase = 'globapi/packages/AllianzTravel/purchase',
  Quoatation = 'globapi/packages/AllianzTravel/quotation',
  PremiumCalculation = 'globapi/packages/AllianzTravel/premiumcalculation',
  DiscountCode = 'globapi/packages/AllianzTravel/discountcode',
  Agreements = 'globapi/packages/AllianzTravel/agreements',
  SaveCalculation = 'globapi/packages/AllianzTravel/save-calculation',
  SendStaticAttachments = 'globapi/packages/AllianzTravel/send-static-attachments',
  CalculationToClientConfirm = 'globapi/packages/AllianzTravel/calculation-to-client-confirm',
  PolicyData = 'globapi/packages/AllianzTravel/use',
  AgentNumber = 'globapi/packages/AllianzTravel/allianz-agents',
  MarketingAssets = 'globapi/packages/AllianzTravel/flayers-agent',
  ImportAgentsFromExcel = 'globapi/packages/AllianzTravel/parse-insured-file',
  BlocklistCheck = 'globapi/packages/AllianzTravel/blocklistcheck',
  KnfLimitCheck = 'globapi/packages/AllianzTravel/knflimitcheck',
}

export enum ApiTravelAgencyEndpoints {
  Config = 'globapi/packages/AllianzTravelTravelAgency/config',
  Purchase = 'globapi/packages/AllianzTravelTravelAgency/purchase',
  Quoatation = 'globapi/packages/AllianzTravelTravelAgency/quotation',
  PremiumCalculation = 'globapi/packages/AllianzTravelTravelAgency/premiumcalculation',
  DiscountCode = 'globapi/packages/AllianzTravelTravelAgency/discountcode',
  Agreements = 'globapi/packages/AllianzTravelTravelAgency/agreements',
  SaveCalculation = 'globapi/packages/AllianzTravelTravelAgency/save-calculation',
  SendStaticAttachments = 'globapi/packages/AllianzTravelTravelAgency/send-static-attachments',
  CalculationToClientConfirm = 'globapi/packages/AllianzTravelTravelAgency/calculation-to-client-confirm',
  PolicyData = 'globapi/packages/AllianzTravelTravelAgency/use',
  MarketingAssets = 'globapi/packages/AllianzTravel/flayers-agent',
  AgentNumber = 'globapi/packages/AllianzTravelTravelAgency/allianz-agents',
  ImportAgentsFromExcel = 'globapi/packages/AllianzTravelTravelAgency/parse-insured-file',
  BlocklistCheck = 'globapi/packages/AllianzTravelTravelAgency/blocklistcheck',
  KnfLimitCheck = 'globapi/packages/AllianzTravelTravelAgency/knflimitcheck',
}

export enum ApiAuthEndpoints {
  Auth = 'AUTH/token',
  AuthLogged = 'GLOBTROTER/api/access/token?grant_type=implicit',
  AuthCheck = 'GLOBTROTER/api/user/logged',
  GlobtroterMenu = 'GLOBTROTER/api/layout/menu',
  GlobtroterUser = 'GLOBTROTER/api/user/details',
  GlobtroterConfig = 'GLOBTROTER/api/user/config',
  isUserLogged = 'GLOBTROTER/SSO/IsUserLogged',
}

export enum ApiRemoteEndpoints {
  Init = 'globapidirect/ext/packages/AllianzTravelDirect/access/:id',
  SendSmsToken = 'globapidirect/ext/packages/AllianzTravelDirect/access/:id/send-sms-token',
  Verify = 'globapidirect/ext/packages/AllianzTravelDirect/access/:id/:otp',
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly isTravelAgency = environment.app === AppType.TravelAgency;
  private readonly apiEndpoints = this.isTravelAgency ? ApiTravelAgencyEndpoints : ApiAgentEndpoints;
  private readonly url: string;
  private isAppDirect = environment.app === AppType.Direct;
  private sessionId = environment.debug ? environment.sso : '';

  constructor(private http: HttpClient, private meta: Meta, private cacheService: CacheService) {
    const urlFromMetaTag: string | undefined = this.meta.getTag('name="application-url-data"')?.content;

    this.url = urlFromMetaTag || environment.baseApiUrl || '';
    this.url = this.url.endsWith('/') ? this.url : `${this.url}/`;
  }

  postAuthToken(body: AuthTokenPayload) {
    const formData = new FormData();
    formData.append('grant_type', body.grant_type);
    formData.append('client_id', body.client_id);
    formData.append('client_secret', body.client_secret);

    return this.http.post<AuthTokenResponse>(this.createUrl(ApiAuthEndpoints.Auth), formData);
  }

  getAuthToken() {
    return this.http.get<AuthTokenResponse>(this.createUrl(ApiAuthEndpoints.AuthLogged));
  }

  getAuthCheck() {
    return this.http.get<AuthCheckResponse>(this.createUrl(ApiAuthEndpoints.AuthCheck));
  }

  getSSOAuthCheck() {
    return this.http.post<AuthCheckResponse>(this.createUrl(ApiAuthEndpoints.isUserLogged), {});
  }

  getConfig() {
    return this.http.get<DefaultConfig>(this.createUrl(ApiDirectEndpoints.Config));
  }

  getAgentConfig() {
    return this.http.get<DefaultConfig>(this.createUrl(this.apiEndpoints.Config));
  }

  postAgentPurchase(body: PurchaseAgentPayload) {
    return this.http.post<PurchaseAgentResponse>(this.createUrl(this.apiEndpoints.Purchase), body);
  }

  getAgreements(pathCodeName: ProcessType) {
    return this.http.get<AgreementsApi>(this.createUrl(`${ApiDirectEndpoints.Agreements}/${pathCodeName}`));
  }

  getAgentAgreements(pathCodeName: ProcessType) {
    return this.http.get<AgreementsApi>(this.createUrl(`${this.apiEndpoints.Agreements}/${pathCodeName}`));
  }

  postQuotation(body: QuotationBody | PremiumCalculationBody, slim: boolean) {
    return this.http.post<QuotationResponse>(this.createUrl(ApiDirectEndpoints.Quoatation), body, { params: { slim } });
  }

  postAgentQuotation(body: AgentQuotationBody | AgentPremiumCalculationBody, slim: boolean) {
    return this.http.post<QuotationResponse>(this.createUrl(this.apiEndpoints.Quoatation), body, { params: { slim } });
  }

  postSaveCalculation(body: SaveCalculationPayload) {
    return this.http.post<SaveCalculationResponse>(this.createUrl(ApiDirectEndpoints.SaveCalculation), body);
  }

  postAgentSaveCalculation(body: AgentSaveCalculationPayload) {
    return this.http.post<SaveCalculationResponse>(this.createUrl(this.apiEndpoints.SaveCalculation), body);
  }

  postAgentSendStaticAttachments(hashCode: string) {
    return this.http.post<SendStaticAttachmentsResponse>(this.createUrl(`${this.apiEndpoints.SendStaticAttachments}/${hashCode}`), null);
  }

  postConfirm(body: ConfirmPayload) {
    return this.http.post<ConfirmResponse>(this.createUrl(ApiDirectEndpoints.Confirm), body);
  }

  getDiscountCode(pathCodeName: ProcessType, discountCode: string) {
    return this.http.get<DiscountCodeResponse>(
      this.createUrl(`${ApiDirectEndpoints.DiscountCode}/${pathCodeName}?discountCode=${discountCode}`),
    );
  }

  getAgentDiscountCode(pathCodeName: ProcessType, discountCode: string) {
    return this.http.get<DiscountCodeResponse>(
      this.createUrl(`${this.apiEndpoints.DiscountCode}/${pathCodeName}?discountCode=${discountCode}`),
    );
  }

  postPremiumCalculation(body: PremiumCalculationBody) {
    return this.http.post<PremiumCalculationApi>(this.createUrl(ApiDirectEndpoints.PremiumCalculation), body);
  }

  postAgentPremiumCalculation(body: AgentPremiumCalculationBody) {
    return this.http.post<PremiumCalculationApi>(this.createUrl(this.apiEndpoints.PremiumCalculation), body);
  }

  getAttachments(packageName: string, hashCode?: string) {
    return this.http.get<AttachmentsResponse>(
      this.createUrl(`${ApiDirectEndpoints.Attachments}/${packageName}/${hashCode ? `?hashCode=${hashCode}` : ''}`),
    );
  }

  getMarketingAssets(): Observable<HttpResponse<Blob>> {
    return this.http.get(this.createUrl(`${this.apiEndpoints.MarketingAssets}`), { observe: 'response', responseType: 'blob' });
  }

  getPaymentsTransaction(transactionGUID: string) {
    return this.http.get<PaymentsTransactionResponse>(this.createUrl(`${ApiDirectEndpoints.PaymentsTransaction}/${transactionGUID}`));
  }

  postPaymentsPolicy(hashCode: string, body: PaymentsPolicyPayload) {
    return this.http.post<PaymentsPolicyResponse>(this.createUrl(`${ApiDirectEndpoints.PaymentsPolicy}/${hashCode}`), body);
  }

  getPaymentsConfig() {
    return this.http.get<PaymentsConfigResponse>(this.createUrl(`${ApiDirectEndpoints.PaymentsConfig}`));
  }

  postSaveContact(body: SaveContactPayload) {
    return this.http.post<SaveContactResponse>(this.createUrl(ApiDirectEndpoints.SaveContact), body);
  }

  getRemoteInit(id: string) {
    return this.http.get<RemoteAccessResponseSuccess | RemoteAccessResponseError>(
      this.createUrl(ApiRemoteEndpoints.Init.replace(':id', id)),
    );
  }

  postRemoteSendSmsToken(id: string) {
    return this.http.post<RemoteSendOtpResponseSuccess | RemoteSendOtpResponseError>(
      this.createUrl(ApiRemoteEndpoints.SendSmsToken.replace(':id', id)),
      {},
    );
  }

  getRemoteVerify(id: string, otp: string) {
    return this.http.get<RemoteCheckSmsTokenResponseSuccess | RemoteCheckSmsTokenResponseError>(
      this.createUrl(ApiRemoteEndpoints.Verify.replace(':id', id).replace(':otp', otp)),
    );
  }

  postCalculationToClientConfirm(body: CalculationToClientConfirmPayload) {
    return this.http.post<CalculationToClientConfirmResponse>(this.createUrl(this.apiEndpoints.CalculationToClientConfirm), body);
  }

  postInsuredImportFile(file: File) {
    const formData = new FormData();
    formData.append('File', file as Blob, file.name);

    return this.http.post<InsuredImportResponse>(this.createUrl(this.apiEndpoints.ImportAgentsFromExcel), formData);
  }

  getPolicyData(policyNumber: string | null, leadId: string | null) {
    const params = new URLSearchParams();

    if (policyNumber !== null) {
      params.append('policyNumber', policyNumber);
    }
    if (leadId !== null) {
      params.append('leadId', leadId);
    }

    return this.http.get<LoadPolicyDataResponse>(this.createUrl(`${this.apiEndpoints.PolicyData}?${params.toString()}`));
  }

  postPhoneNumberForValidation(phoneNumber: string): Observable<AsyncValidatorResponse> {
    return this.postCached<AsyncValidatorResponse>(this.createUrl(ApiDirectEndpoints.PhoneNumberValidate), { phoneNumber }, true);
  }

  postEmailForValidation(email: string): Observable<AsyncValidatorResponse> {
    return this.postCached<AsyncValidatorResponse>(this.createUrl(ApiDirectEndpoints.EmailValidate), { email }, true);
  }

  getAgentNumberForValidation(agentNumber: string, processKind: ProcessType | null): Observable<AllianzAgentsResponse> {
    return this.http.get<AllianzAgentsResponse>(
      this.createUrl(`${this.apiEndpoints.AgentNumber}/${agentNumber}${processKind ? '?path=' + processKind : ''}`),
    );
  }

  postBlocklistCheck(body: BlocklistPolicyholderPayload): Observable<BlockValidatorResponse> {
    return this.http.post<BlockValidatorResponse>(
      this.createUrl(this.isAppDirect ? ApiDirectEndpoints.BlocklistCheck : this.apiEndpoints.BlocklistCheck),
      body,
    );
  }

  postKnfLimitCheck(body: KnfLimitCheckPayload): Observable<BlockValidatorResponse> {
    return this.http.post<BlockValidatorResponse>(this.createUrl(this.apiEndpoints.KnfLimitCheck), body);
  }

  getGlobtroterMenu(): Observable<GlobtroterMenuResult> {
    return this.http.get<GlobtroterMenuResult>(this.createUrl(ApiAuthEndpoints.GlobtroterMenu));
  }

  getGlobtroterUser(): Observable<GlobtroterUserResult> {
    return this.http.get<GlobtroterUserResult>(this.createUrl(ApiAuthEndpoints.GlobtroterUser));
  }

  getGlobtroterConfig(): Observable<GlobtroterConfigResult> {
    return this.http.get<GlobtroterConfigResult>(this.createUrl(ApiAuthEndpoints.GlobtroterConfig));
  }

  postCached<T>(url: string, body: any, useCache: boolean = true): Observable<T> {
    if (useCache) {
      const cacheKey = `${url}-${JSON.stringify(body)}`;
      const cachedData = this.cacheService.get(cacheKey);

      if (cachedData?.data || this.cacheService.isPendingRequest(cacheKey)) {
        return of(cachedData?.data as T);
      }

      this.cacheService.set(cacheKey, null, true);
    }

    return this.http.post<T>(url, body).pipe(
      tap((data) => {
        if (useCache) {
          const cacheKey = `${url}-${JSON.stringify(body)}`;
          this.cacheService.set(cacheKey, data, false);
        }
      }),
      catchError((error: AppError) => {
        const cacheKey = `${url}-${JSON.stringify(body)}`;
        this.cacheService.set(cacheKey, error, false);

        return throwError(() => new AppError('api', error.code, error.message));
      }),
    );
  }

  setSessionId(newSessionId: string) {
    this.sessionId = newSessionId;
  }

  private createUrl(endpoint: string): string {
    if (Object.values(ApiAuthEndpoints).some((value) => value === endpoint)) {
      return this.url + (environment.debug ? `ssoproxy/apis/${this.sessionId}/` : '') + endpoint;
    }

    return this.url + endpoint;
  }
}
